import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiInfo, FiX } from 'react-icons/fi'
import { createContext, useState, useCallback, type ReactNode } from 'react'

// ============================================================
//  Toast Context
//  Global toast notification system
// ============================================================

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface Toast {
    id: string
    message: string
    type: ToastType
    duration?: number
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType, duration?: number) => void
    hideToast: (id: string) => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([])

    const hideToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id))
    }, [])

    const showToast = useCallback((message: string, type: ToastType = 'info', duration: number = 3000) => {
        const id = `toast-${Date.now()}-${Math.random()}`
        const newToast: Toast = { id, message, type, duration }
        
        setToasts(prev => [...prev, newToast])

        // Auto-dismiss after duration
        if (duration > 0) {
            setTimeout(() => {
                hideToast(id)
            }, duration)
        }
    }, [hideToast])

    return (
        <ToastContext.Provider value={{ showToast, hideToast }}>
            {children}
            <ToastContainer toasts={toasts} onClose={hideToast} />
        </ToastContext.Provider>
    )
}

// ─── Toast Container ──────────────────────────────────────────────────────────
function ToastContainer({ toasts, onClose }: { toasts: Toast[]; onClose: (id: string) => void }) {
    if (toasts.length === 0) return null

    return (
        <div className="toast-container">
            {toasts.map(toast => (
                <ToastItem key={toast.id} toast={toast} onClose={onClose} />
            ))}
        </div>
    )
}

// ─── Toast Item ───────────────────────────────────────────────────────────────
function ToastItem({ toast, onClose }: { toast: Toast; onClose: (id: string) => void }) {
    const getIcon = () => {
        switch (toast.type) {
            case 'success':
                return <FiCheckCircle size={20} aria-hidden="true" />
            case 'error':
                return <FiAlertCircle size={20} aria-hidden="true" />
            case 'warning':
                return <FiAlertTriangle size={20} aria-hidden="true" />
            case 'info':
            default:
                return <FiInfo size={20} aria-hidden="true" />
        }
    }

    return (
        <div className={`toast-item toast-${toast.type}`}>
            <div className="toast-icon">{getIcon()}</div>
            <div className="toast-message">{toast.message}</div>
            <button
                type="button"
                className="toast-close"
                onClick={() => onClose(toast.id)}
                aria-label="Close"
            >
                <FiX size={16} aria-hidden="true" />
            </button>
        </div>
    )
}
